// utils/imageAttach.js
// Helper bersama untuk lampiran gambar di sisi klien.
// Dipakai oleh:
//   - NotesPlain.jsx  → sisip gambar sebagai data URI ke isi catatan
//   - AIChat.js       → lampirkan gambar ke pertanyaan mode Coding (Bedrock)
//
// File ini sengaja PURE (tanpa React / tanpa import context) agar bebas dari
// risiko circular import.

export const IMG_MAX_DIM    = 1600;
export const IMG_MAX_BYTES  = 5 * 1024 * 1024;   // batas file sumber
export const IMG_KEEP_AS_IS = 200 * 1024;        // di bawah ini tidak perlu re-encode

// Android kadang memberi `File.type` kosong atau 'application/octet-stream'
// (tergantung penyedia: Gallery, Samsung My Files, Drive). Jangan hanya
// percaya mimetype — jatuh ke ekstensi nama file.
const IMG_EXT_RE = /\.(png|jpe?g|webp|gif|bmp|heic|heif|avif)$/i;

export function isImageFile(f) {
  if (!f) return false;
  if (/^image\//i.test(f.type || '')) return true;
  return IMG_EXT_RE.test(f.name || '');
}

export function humanBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

function readAsDataUri(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(new Error('read failed'));
    fr.readAsDataURL(file);
  });
}

// Gambar → data URI, dengan downscale di klien.
// Kegagalan DECODE dilempar (IMG_DECODE_FAILED): kalau browser tidak bisa
// men-decode di sini, <img> juga tidak akan bisa menampilkannya — kasus umum
// HEIC/HEIF dari kamera HP. Kegagalan DOWNSCALE saja aman di-fallback.
export async function imageFileToDataUri(file) {
  const raw = await readAsDataUri(file);
  const isGif = /^image\/gif$/i.test(file.type || '') || /\.gif$/i.test(file.name || '');
  if (isGif || file.size <= IMG_KEEP_AS_IS) return raw;
  let img;
  try {
    img = await new Promise((resolve, reject) => {
      const im = new window.Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('decode failed'));
      im.src = raw;
    });
  } catch (_) {
    const err = new Error('IMG_DECODE_FAILED');
    err.fileName = file.name || '';
    throw err;
  }
  try {
    const scale = Math.min(1, IMG_MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    if (scale === 1 && /^image\/jpe?g$/i.test(file.type || '')) return raw;
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    // PNG transparan → JPEG akan jadi hitam; beri latar putih dulu.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const out = cv.toDataURL('image/jpeg', 0.82);
    return out.length < raw.length ? out : raw;
  } catch (_) {
    // Decode BERHASIL, hanya downscale yang gagal → aman pakai aslinya.
    return raw;
  }
}

// data URI → { format, b64 } untuk Bedrock Converse.
// Converse hanya menerima png/jpeg/gif/webp; format lain dikembalikan null
// agar pemanggil bisa menolak dengan pesan yang jelas.
const CONVERSE_FORMATS = new Set(['png', 'jpeg', 'gif', 'webp']);

export function dataUriToImagePart(uri) {
  const m = /^data:image\/([a-z0-9.+-]+);base64,(.*)$/i.exec(String(uri || ''));
  if (!m) return null;
  const fmt = m[1].toLowerCase() === 'jpg' ? 'jpeg' : m[1].toLowerCase();
  if (!CONVERSE_FORMATS.has(fmt)) return null;
  return { format: fmt, b64: m[2] };
}

export function b64Bytes(b64) {
  return Math.floor(String(b64 || '').length * 0.75);
}

// ── [THUMB] Miniatur untuk JEJAK RIWAYAT ──────────────────────────────────────
// Dipakai agar lampiran gambar tetap terlihat di transkrip dan di AI Chat
// History, bukan lenyap begitu pesan terkirim.
//
// Ini SENGAJA bukan gambar aslinya. Yang dikirim ke model tetap versi penuh
// (IMG_MAX_DIM 1600); yang disimpan ke DB hanya miniatur ini. Gambar 131 KB
// yang disimpan utuh akan menumpuk cepat di ai_chat_history — dan karena backup
// memakai `VACUUM INTO` yang menyalin SELURUH database, setiap backup ikut
// membengkak. Miniatur 160px ±5–10 KB membuat biayanya terkendali.
export const THUMB_MAX_DIM   = 160;
export const THUMB_MAX_CHARS = 30 * 1024;   // batas keras data URI hasil

// Versi PREVIEW — yang dibuka saat miniatur diklik. 640px cukup untuk membaca
// judul dan angka besar pada screenshot dashboard, sementara 160px tidak.
// Sengaja BUKAN gambar asli (1600px, ±130 KB): pada volume harian yang wajar
// selisihnya ratusan MB per tahun di DB, dan itu ikut tersalin setiap backup.
export const PREVIEW_MAX_DIM   = 640;
export const PREVIEW_MAX_CHARS = 120 * 1024;

// Inti pengecil. Mengembalikan data URI, atau '' bila gagal / melewati batas.
// Kegagalan TIDAK dilempar: gambar turunan cuma pelengkap, dan pertanyaan user
// tidak boleh batal terkirim hanya karena versi kecilnya tak bisa dibuat.
async function scaleToDataUri(uri, maxDim, quality, maxChars) {
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new window.Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('decode failed'));
      im.src = uri;
    });
    const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    // PNG transparan → JPEG akan jadi hitam; beri latar putih dulu.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const out = cv.toDataURL('image/jpeg', quality);
    // Batas keras: gambar sangat berderau bisa tetap besar walau kecil dimensinya.
    return out.length <= maxChars ? out : '';
  } catch (_) {
    return '';
  }
}

// ── [THUMB] Miniatur untuk JEJAK RIWAYAT ──────────────────────────────────────
// Dipakai agar lampiran gambar tetap terlihat di transkrip dan di AI Chat
// History, bukan lenyap begitu pesan terkirim.
//
// Ini SENGAJA bukan gambar aslinya. Yang dikirim ke model tetap versi penuh
// (IMG_MAX_DIM 1600); yang disimpan ke DB hanya turunannya. Gambar 131 KB yang
// disimpan utuh akan menumpuk cepat di ai_chat_history — dan karena backup
// memakai `VACUUM INTO` yang menyalin SELURUH database, setiap backup ikut
// membengkak.
//
// Dua ukuran disimpan, dan itu disengaja: miniatur SELALU dirender di daftar
// pesan, sedangkan preview hanya ditampilkan saat diklik. Kalau hanya preview
// yang disimpan, riwayat berisi 20 gambar harus merender 20 × 40 KB sekaligus.
export async function makeThumbDataUri(uri) {
  return scaleToDataUri(uri, THUMB_MAX_DIM, 0.6, THUMB_MAX_CHARS);
}

export async function makePreviewDataUri(uri) {
  return scaleToDataUri(uri, PREVIEW_MAX_DIM, 0.7, PREVIEW_MAX_CHARS);
}
